import type { FC } from 'react';
import React, { createElement, useCallback } from 'react';

import { Button } from '../../fluent9/components/Button';
import FluentUISystemIcon, {
  PanelRightContract20Filled,
  PanelRightExpand20Regular,
  bundleIcon,
} from '../FluentUISystemIcon';

import type { AppLayoutContextValue } from './AppLayoutContextProvider.types';
import { useClassName } from './SidebarPanelButton.styles';
import type { SidebarPanelButtonProps } from './SidebarPanelButton.types';
import { useAppLayoutContext } from './hooks';

// For the default open icon, we don't want to change the icon on hover so using the same icon for both states.
const DefaultOpenIcon = bundleIcon(PanelRightExpand20Regular, PanelRightExpand20Regular);

const SidebarPanelButton: FC<SidebarPanelButtonProps> = ({
  openIcon = DefaultOpenIcon,
  closeIcon = PanelRightContract20Filled,
  closeIconColor,
}) => {
  const className = useClassName();
  const { openContentSidebarAriaLabel, openSidebarPanel, closeSidebarPanel, isSidebarPanelOpen } =
    useAppLayoutContext() as AppLayoutContextValue;

  const toggleSidebarPanel = useCallback(() => {
    if (!isSidebarPanelOpen) {
      openSidebarPanel();
    } else {
      closeSidebarPanel();
    }
  }, [isSidebarPanelOpen, openSidebarPanel, closeSidebarPanel]);

  const icon = isSidebarPanelOpen ? (
    <FluentUISystemIcon Icon={closeIcon} color={closeIconColor} />
  ) : (
    createElement(openIcon)
  );

  return (
    <Button
      appearance="transparent"
      aria-label={openContentSidebarAriaLabel}
      icon={icon}
      onClick={toggleSidebarPanel}
      className={className}
    />
  );
};

export default SidebarPanelButton;
